import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Sheet, Stack, Typography } from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { useNavigate, useParams } from "react-router-dom";
import AdminEventForm, { AdminEventFormValues } from "../components/Admin/AdminEventForm";
import { fetchAdminEvent, updateAdminEvent, AdminEvent } from "../api";
import PageHero from "../components/common/PageHero";

export default function AdminEventEditPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminEvent(eventId as string);
        if (mounted) {
          setEvent(data ?? null);
        }
      } catch (err) {
        console.error("Failed to load event", err);
        if (mounted) {
          setError("We couldn’t load the seva details. Please try again.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (eventId) {
      load();
    }

    return () => {
      mounted = false;
    };
  }, [eventId]);

  const handleSubmit = async (data: AdminEventFormValues) => {
    if (!eventId) return;
    setIsSubmitting(true);
    try {
      await updateAdminEvent(eventId, data);
      navigate("/events");
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroTitle = event ? `Editing ${event.title}` : "Edit seva event";
  const heroDescription = event
    ? "Adjust the schedule, poster, and seva opportunities before devotees arrive."
    : "We’re gathering the current details so you can update this celebration.";

  return (
    <Box>
      <PageHero
        eyebrow="Festival planning"
        title={heroTitle}
        description={heroDescription}
        actions={
          <>
            <Button
              variant="soft"
              color="neutral"
              startDecorator={<CalendarMonthRoundedIcon />}
              onClick={() => navigate("/events")}
            >
              View all events
            </Button>
            <Button
              variant="plain"
              color="neutral"
              startDecorator={<ArrowBackRoundedIcon />}
              onClick={() => navigate(-1)}
            >
              Go back
            </Button>
          </>
        }
      />

      {loading ? (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size="md" />
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Gathering seva details…
          </Typography>
        </Sheet>
      ) : error ? (
        <Sheet
          variant="soft"
          color="danger"
          sx={{ borderRadius: "xl", p: 3, display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Unable to load event
          </Typography>
          <Typography level="body-sm">{error}</Typography>
          <Button variant="soft" color="neutral" onClick={() => navigate("/events")}>Return to events</Button>
        </Sheet>
      ) : event ? (
        <AdminEventForm
          initial={event}
          onSubmit={handleSubmit}
          onCancel={() => navigate("/events")}
          isSubmitting={isSubmitting}
        />
      ) : (
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "xl", p: 3, display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Event not found
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            The seva you’re looking for may have been removed.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="soft" color="neutral" onClick={() => navigate("/events")}>Go to events</Button>
            <Button variant="plain" color="neutral" onClick={() => navigate(-1)}>Go back</Button>
          </Stack>
        </Sheet>
      )}
    </Box>
  );
}
